import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';

const RouterStep2Verify = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  
  const router = location.state?.router;
  const [check1, setCheck1] = useState(false);
  const [check2, setCheck2] = useState(false);

  if (!router) {
    navigate('/routers');
    return null;
  }

  const canContinue = check1 && check2;

  const handleNext = () => {
    if (!canContinue) {
      alert('Por favor, completa las verificaciones antes de continuar');
      return;
    }
    
    navigate('/router-step3-connect', { 
      state: { 
        router: router
      } 
    });
  };

  const handleBack = () => {
    navigate('/router-step1-script', { 
      state: { 
        router: router
      } 
    });
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-50'} p-6`}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>
            📡 Configurar Router #{router.router_index}
          </h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Paso 2 de 3: Verificar Conexión VPN
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-green-600 rounded-full"></div>
            <div className="flex-1 h-2 bg-blue-600 rounded-full"></div>
            <div className={`flex-1 h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-300'} rounded-full`}></div>
          </div>
          <div className="flex justify-between mt-2 text-xs">
            <span className="text-green-600 font-semibold">✓ Script</span>
            <span className="text-blue-600 font-semibold">Verificar</span>
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Conectar</span>
          </div>
        </div>

        {/* Instrucciones */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 mb-6`}>
          <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            📋 Instrucciones
          </h3>
          <ol className={`space-y-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            <li className="flex gap-3">
              <span className="font-bold text-blue-600 flex-shrink-0">1.</span>
              <span>Abre <strong>WinBox</strong> y conéctate a tu MikroTik</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-blue-600 flex-shrink-0">2.</span>
              <span>Ve a <strong>Terminal</strong> (menú "New Terminal")</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-blue-600 flex-shrink-0">3.</span>
              <span>Pega el script que copiaste</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-blue-600 flex-shrink-0">4.</span>
              <span>Presiona <strong>Enter</strong> para ejecutar</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-blue-600 flex-shrink-0">5.</span>
              <span>Ve a <strong>Interfaces → L2TP Client</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-blue-600 flex-shrink-0">6.</span>
              <span>Verifica que <strong>VPN-ETWEB</strong> tenga status: <span className="text-green-600 font-bold">connected</span></span>
            </li>
          </ol>
        </div>

        {/* Verificaciones */}
        <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-xl shadow-lg p-6 mb-6`}>
          <h3 className={`text-lg font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            ✅ Verificar antes de continuar
          </h3>
          
          <div className="space-y-4">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={check1}
                onChange={(e) => setCheck1(e.target.checked)}
                className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className={`text-sm ${isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
                Pegué el script en la terminal del MikroTik y presioné Enter
              </span>
            </label>
            
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={check2}
                onChange={(e) => setCheck2(e.target.checked)}
                className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <span className={`text-sm ${isDark ? 'text-gray-300 group-hover:text-white' : 'text-gray-700 group-hover:text-gray-900'}`}>
                La conexión <strong>VPN-ETWEB</strong> aparece como <span className="text-green-600 font-bold">"connected"</span> en Interfaces → L2TP Client
              </span>
            </label>
          </div>
        </div>

        {/* Ayuda */}
        <div className={`${isDark ? 'bg-orange-900/20 border-orange-500/30' : 'bg-orange-50 border-orange-200'} border rounded-xl p-6 mb-6`}>
          <h4 className={`font-semibold mb-2 ${isDark ? 'text-orange-400' : 'text-orange-900'}`}>
            💡 ¿No aparece como "connected"?
          </h4>
          <p className={`text-sm ${isDark ? 'text-orange-200' : 'text-orange-800'}`}>
            Verifica que tu MikroTik tenga acceso a internet. La VPN necesita conectarse al servidor 45.32.207.196.
          </p>
        </div>

        {/* Botones */}
        <div className="flex gap-4">
          <button
            onClick={handleBack}
            className={`px-6 py-3 rounded-lg font-semibold ${
              isDark ? 'bg-gray-700 hover:bg-gray-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            <ArrowLeft className="w-5 h-5 inline mr-2" />
            Atrás
          </button>
          <button
            onClick={handleNext}
            disabled={!canContinue}
            className={`flex-1 px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 ${
              canContinue
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-gray-400 text-gray-200 cursor-not-allowed'
            }`}
          >
            Siguiente: Conectar Router
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default RouterStep2Verify;
